import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import LockIcon from '@mui/icons-material/Lock';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import type { CouponPreview } from '@lov/shared';

interface TicketCouponCardProps {
  coupon: CouponPreview;
  onApply: (code: string) => void;
  isApplying?: boolean;
  isApplied?: boolean;
  variant?: 'default' | 'compact';
}

export function TicketCouponCard({
  coupon,
  onApply,
  isApplying = false,
  isApplied = false,
  variant = 'default',
}: TicketCouponCardProps) {
  const isLocked = coupon.status === 'locked' || coupon.status === 'nearly_unlocked';
  const isNearlyUnlocked = coupon.status === 'nearly_unlocked';

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleApply = () => {
    if (!isLocked && !isApplying) {
      onApply(coupon.code);
    }
  };

  return (
    <Box
      role="article"
      aria-label={`Coupon: ${coupon.code}, saves ${coupon.estimatedSavings}`}
      sx={{
        display: 'flex',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: isLocked ? 'grey.300' : 'primary.light',
        backgroundColor: isLocked ? 'grey.50' : 'background.paper',
        opacity: isLocked && !isNearlyUnlocked ? 0.8 : 1,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: isLocked ? 'grey.400' : 'primary.main',
          boxShadow: isLocked ? 1 : 3,
        },
      }}
    >
      {/* Perforated edge */}
      <Box
        sx={{
          width: 16,
          backgroundColor: isLocked ? 'grey.200' : 'primary.main',
          backgroundImage: `radial-gradient(circle, white 4px, transparent 4px)`,
          backgroundSize: '16px 16px',
          backgroundPosition: 'center',
        }}
      />

      {/* Content */}
      <Box sx={{ flex: 1, p: 2 }}>
        {/* Header row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isLocked ? (
              <LockIcon data-testid="lock-icon" fontSize="small" color="disabled" />
            ) : (
              <LocalOfferIcon fontSize="small" color="primary" />
            )}
            <Typography variant="body1" fontWeight={700} color={isLocked ? 'text.secondary' : 'primary.main'}>
              {coupon.code}
            </Typography>
          </Box>
          <Typography variant="body2" fontWeight={600} color="success.main">
            {coupon.estimatedSavings && `Save ${formatCurrency(coupon.estimatedSavings)}`}
          </Typography>
        </Box>

        {/* Name and description */}
        <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
          {coupon.name}
        </Typography>
        {variant === 'default' && coupon.description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {coupon.description}
          </Typography>
        )}

        {/* Progress bar for locked coupons */}
        {isLocked && coupon.percentToUnlock !== null && (
          <Box sx={{ mb: 1.5 }}>
            <LinearProgress
              variant="determinate"
              value={coupon.percentToUnlock}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: isNearlyUnlocked ? 'warning.main' : 'grey.400',
                },
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {isNearlyUnlocked && 'Almost there! '}
              {formatCurrency(coupon.amountNeededToUnlock || 0)} away from saving {formatCurrency(coupon.estimatedSavings || 0)}
            </Typography>
          </Box>
        )}

        {/* Action button */}
        <Button
          variant={isLocked ? 'outlined' : 'contained'}
          size="small"
          fullWidth
          disabled={isLocked || isApplying || isApplied}
          onClick={handleApply}
          aria-busy={isApplying}
          sx={{ mt: 1 }}
        >
          {isApplying ? (
            <CircularProgress size={20} color="inherit" />
          ) : isApplied ? (
            'Applied'
          ) : isLocked ? (
            'Locked'
          ) : (
            `Save ${formatCurrency(coupon.estimatedSavings || 0)} now`
          )}
        </Button>
      </Box>
    </Box>
  );
}
